import { createContext, useContext, type ReactNode } from "react"
import type { Me, Permission } from "./types"

const MeContext = createContext<Me | null>(null)

export function MeProvider({ me, children }: { me: Me; children: ReactNode }) {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>
}

export function useCurrentMe(): Me {
  const me = useContext(MeContext)
  if (!me) throw new Error("useCurrentMe outside MeProvider")
  return me
}

/** Mirrors the backend check. The UI hides what you can't do; the API still enforces it. */
export function useCan() {
  const me = useCurrentMe()
  const perms = new Set(me.staff?.permissions ?? [])
  return (p: Permission) => perms.has(p)
}

export function Can({ perm, children, fallback = null }: { perm: Permission; children: ReactNode; fallback?: ReactNode }) {
  return useCan()(perm) ? <>{children}</> : <>{fallback}</>
}

/** Order number as set in Settings → General (Order ID format), e.g. #1001. */
export function useOrderName() {
  const me = useCurrentMe()
  const pre = me.store.order_prefix ?? "#"
  const suf = me.store.order_suffix ?? ""
  return (n: number | string | null | undefined) => (n == null ? "—" : `${pre}${n}${suf}`)
}
